import React from 'react'
import { useParams } from 'react-router-dom'

import { Box, Stack } from '@mui/material'

import { Card, Skeleton } from '@repo/ui'
import { ActionDropdown, Header, QueryErrorState, STATUS_TOKEN_H } from 'components'
import { NARROW_COLUMN_WIDTH } from 'config'
import { useSnackbar } from 'contexts'
import { useDeferredLoading } from 'hooks'
import { useInternalSupportReply, useInternalSupportRequest } from 'hooks/api'
import { SUPPORT_REQUEST_STATUSES_INTERNAL } from 'constants/statuses'

import { requestTitle } from 'pages/support/SupportRequestPage/SupportRequestPage'
import { ReplyComposer } from 'pages/support/SupportRequestPage/ReplyComposer'
import { RequestThread } from 'pages/support/SupportRequestPage/RequestThread'

import { useRequestActions } from '../InternalSupportPage/requestActions'

/**
 * One support request and its conversation, from Vera's side of the desk.
 *
 * ## Why this exists as a page rather than an accordion
 *
 * The queue used to expand a request in place. That made the internal view a
 * structurally different thing from `/support/:id`, which is what the customer
 * reads — and the differences all cut against the staffer:
 *
 *   - **No address.** An inline panel has no URL, so a request could not be linked
 *     in Slack, in a Jira ticket, or to a colleague. The one surface whose whole
 *     job is "somebody else needs to look at this" could not be pointed at.
 *   - **No way back, because there was no way in.** Reading a request meant losing
 *     your place in a list you had just filtered; closing it was the only exit.
 *   - **A thread inside a list row.** The conversation rendered in a card that was
 *     itself a row of a card list, so a long thread pushed every request below it
 *     off the screen.
 *
 * `RequestThread` was already written to be shared (see its header note) — the
 * accordion just never gave it a page to live on. This is that page.
 *
 * ## What is deliberately NOT the same as the customer's page
 *
 * `preferBoardStatus` is off. On the customer side a promoted feature shows its
 * BOARD status, so the list and the detail agree. Here a staffer is looking at the
 * request status precisely because they act on it, so showing the board status
 * instead would hide the field they came to change.
 *
 * `showCompany` is on, for the obvious reason: the queue is cross-company, so "who
 * is this" is the first question, and it is one the customer never has to ask.
 */
export const InternalSupportRequestPage = () => {
  const { id } = useParams<{ id: string }>()
  const { open: notification } = useSnackbar()

  const { data: request, isLoading, isError, error, refetch } = useInternalSupportRequest(id)
  const { mutateAsync: postReply, isPending: replying } = useInternalSupportReply(id ?? '')
  const requestActions = useRequestActions()

  // `!request` included: a query that has settled without data must keep the
  // skeleton rather than fall through to a page rendered against `undefined`.
  const pending = isLoading || !request
  const showSkeleton = useDeferredLoading(pending)

  /*
    `mutateAsync`, not `mutate`, because `ReplyComposer` clears itself only when the
    promise RESOLVES — that is how a failed send keeps the staffer's draft (and their
    uploaded files) on screen instead of silently discarding them. The composer
    deliberately swallows the rejection and leaves the message to us, so the toast
    below is not optional: without it a failed reply would fail silently.
  */
  const handleReply = async (payload: { body: string; attachmentUrls: string[] }) => {
    try {
      await postReply(payload)
    } catch (e: any) {
      notification({ type: 'error', title: e?.message || "Couldn't post your reply." })
      throw e
    }
  }

  // Before the loading branch, for the reason the customer page spells out:
  // `isLoading || !request` stays true forever on a 403 or a 404, so an error would
  // otherwise sit on a permanent skeleton saying nothing the user can act on.
  if (isError) {
    return (
      <Box sx={{ maxWidth: NARROW_COLUMN_WIDTH, mx: 'auto' }}>
        <Header title="Request" breadcrumbsOveride="Support" />
        <QueryErrorState
          error={error}
          subject="this request"
          permissionLabel="Manage support"
          onRetry={() => refetch()}
        />
      </Box>
    )
  }

  if (showSkeleton) {
    return (
      <Box sx={{ maxWidth: NARROW_COLUMN_WIDTH, mx: 'auto' }}>
        {/* Bars rather than words, and a SUBTITLE bar as well as a title one — the
            loaded page passes a subtitle, and a skeleton without one renders a 49px
            title row against the loaded page's 53.2, sliding every region below it
            down on load. Heights are the loaded elements' own line boxes. */}
        <Header
          title={<Skeleton width={360} height={31.2} />}
          subtitle={<Skeleton width={220} height={18} />}
          breadcrumbsOveride={<Skeleton width={48} height={20} />}
        />
        <Stack spacing={4}>
          <Card sx={{ p: 4, mb: 0 }}>
            <Stack spacing={3}>
              <Stack direction="row" spacing={3} alignItems="center">
                <Skeleton width={68} height={STATUS_TOKEN_H} />
                <Skeleton width={56} height={STATUS_TOKEN_H} />
                <Skeleton width={36} height={18} />
                <Box sx={{ flex: 1 }} />
                <Skeleton width={138} height={18} />
              </Stack>
              <Skeleton lines={1} />
            </Stack>
          </Card>
          {/* `ReplyComposer`, mirrored control for control rather than as one block:
              an 18px field label, the 8px gap `FieldLabel` owns, the 118px four-row
              textarea, then the attachments label + its 140px dropzone, then the 40px
              send button on its own right-aligned row. */}
          <Card sx={{ p: 4, mb: 0 }}>
            <Stack spacing={3}>
              <Box>
                <Skeleton width={90} height={18} />
                <Skeleton height={118} sx={{ mt: 2 }} />
              </Box>
              <Box>
                <Skeleton width={130} height={18} />
                <Skeleton height={140} sx={{ mt: 2 }} />
              </Box>
              <Stack direction="row" justifyContent="flex-end">
                <Skeleton width={108} height={40} />
              </Stack>
            </Stack>
          </Card>
        </Stack>
      </Box>
    )
  }

  // Still loading, inside the skeleton's grace period. Everything below
  // dereferences `request`, so this renders nothing rather than crashing.
  if (pending) return null

  return (
    <Box sx={{ maxWidth: NARROW_COLUMN_WIDTH, mx: 'auto' }}>
      <Header
        title={requestTitle(request.body)}
        // Company in the subtitle, not just in the thread: on a cross-company queue
        // the brand is part of identifying WHICH request this is, and the subtitle is
        // what a browser tab and a back-link show.
        subtitle={`Request #${request.reference} · ${request.companyName ?? 'Unknown company'}`}
        /*
          Back-navigation is the breadcrumb plus the referrer link `Header` renders
          on its own — never a hand-rolled "← Back" (§42, and the standing rule that
          the breadcrumb is the single back-affordance even on deep-link entry). The
          queue passes `useReferrerState()` when it navigates here, so arriving from
          a filtered list returns to that list with its filters intact; arriving from
          a pasted URL still has the trail.

          `breadcrumbsOveride` replaces the LEAF segment, which would otherwise fall
          back to the raw uuid in the path.
        */
        breadcrumbsOveride={`#${request.reference}`}
        rightComponents={<ActionDropdown items={requestActions(request)} />}
      />

      <Stack spacing={4}>
        {/* The SAME component `/support/:id` renders. */}
        <RequestThread
          request={request}
          showCompany
          statuses={SUPPORT_REQUEST_STATUSES_INTERNAL}
        />

        {/* The SAME composer the brand thread uses — text plus attachments, with the
            upload orchestration (on-submit, `allSettled`, a failed upload that must not
            eat the draft) solved once. A hand-rolled staff reply box would keep the
            textarea and quietly lose all three, and staff are the side that asks for
            screenshots. Only the label differs. */}
        <ReplyComposer
          label="Reply as Vera"
          placeholder="Answer here — this is the reply surface, not Slack."
          onSend={handleReply}
          isPending={replying}
        />
      </Stack>
    </Box>
  )
}
